import { LinkAction } from '@today/ui-components'
import { Button } from '@today/ui-primitives'
import type { ReactElement, ReactNode } from 'react'
import type { InheritedRowView, Translate, WarningView } from './content-types'

/**
 * Props for {@link ProductLegalTextsView}.
 *
 * @property own - The "Assigned to this product" list (an {@link AssignmentListView}).
 * @property preview - The resolve preview (a {@link ResolvePreviewView}).
 * @property onAttach - Present only with `content.assign`.
 */
export interface ProductLegalTextsViewProps {
	t: Translate
	warnings: WarningView[]
	inherited: InheritedRowView[]
	own: ReactNode
	preview: ReactNode
	error: string | null
	onAttach?: () => void
	onOpenTenantDefaults: () => void
}

const warningText = (t: Translate, w: WarningView) =>
	w.kind === 'missing'
		? t('legal.warningMissing', { code: w.code, locales: w.locales.join(', ') })
		: t('legal.warningExpiring', { code: w.code, locale: w.locale, date: w.date })

/** The product editor's "Legal texts" tab: warnings, own and inherited texts, preview (organism). */
export function ProductLegalTextsView(props: ProductLegalTextsViewProps): ReactElement {
	const { t } = props
	return (
		<div className="flex flex-col gap-6">
			{props.error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{props.error}
				</p>
			) : null}
			{props.warnings.length ? (
				<ul aria-label={t('legal.warnings')} className="flex flex-col gap-1">
					{props.warnings.map((w) => (
						<li
							key={`${w.kind}-${w.code}-${w.kind === 'expiring' ? w.locale : ''}`}
							className="text-ds-sm text-ds-danger"
						>
							{warningText(t, w)}
						</li>
					))}
				</ul>
			) : null}
			{props.onAttach ? (
				<div>
					<Button onPress={props.onAttach}>{t('assignments.attach')}</Button>
				</div>
			) : null}
			{props.own}
			<section className="flex flex-col gap-2" aria-label={t('legal.inherited')}>
				<header className="flex items-center gap-3">
					<h2 className="text-ds-lg font-semibold text-ds-text">{t('legal.inherited')}</h2>
					<LinkAction onPress={props.onOpenTenantDefaults}>{t('legal.tenantDefaultsLink')}</LinkAction>
				</header>
				{props.inherited.length === 0 ? (
					<p className="text-ds-sm text-ds-text-muted">{t('assignments.empty')}</p>
				) : (
					<ul className="flex flex-col gap-1">
						{props.inherited.map((row) => (
							<li key={row.id} className="text-ds-sm">
								{[row.code, row.name, row.replacedByCode ? t('legal.replacedBy', { code: row.replacedByCode }) : null]
									.filter(Boolean)
									.join(' · ')}
							</li>
						))}
					</ul>
				)}
			</section>
			{props.preview}
		</div>
	)
}
